import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Checkbox,
  debounce,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useMemo, useState, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { StationsLocationsTypes } from '../../components/features/Map/types';
import { FilterChip, FilterSwitch } from '../../components/features/Map/FilterControls';
import { ocppService } from '../../services/ocpp/OcppService';
import { StationTypes } from '../Station/types';
import { Card } from '../../components/common/Card';
import { Pagination } from '../../components/common/Pagination';
import { usePaginationState } from '../../hooks/usePaginationState';
import { CardEnum } from '../../components/common/Card/types';
import { StationCardTypes } from '../../components/common/Card/StationCard/types';
import { StationsResponse } from '../../services/ocpp/types';
import { ConnectorTypeEnum } from '@shared/enums/connector-type';
import { TitleWithBackButton } from '../../components/common/TitleWithBackButton/TitleWithBackTutton';
import {
  MapFilters,
  countActiveFilters,
  hasAnyFilter,
} from '../../components/features/Map/filterTypes';
import { useStationSearch } from '../../components/features/Map/useStationSearch';

// Connector types to show as filter chips (quick access)
const CONNECTOR_TYPE_CHIPS = [
  ConnectorTypeEnum['Type 2'],
  ConnectorTypeEnum.CCS2,
  ConnectorTypeEnum.CHAdeMO,
  ConnectorTypeEnum['Type 1'],
];

// All connector types for the drawer
const ALL_CONNECTOR_TYPES = Object.values(ConnectorTypeEnum);

interface AutocompleteOption {
  address: string;
  code?: number;
  _id?: string;
}

type StationListItem = StationsLocationsTypes | StationTypes;

// Search results without an address or id can't route to a station page or
// be given a stable list key, so they're dropped before rendering.
const isStationLocation = (station: StationsLocationsTypes): boolean =>
  Boolean(station.address) && Boolean(station._id);

// Builds the Station_card's data from whichever shape the list currently
// holds (search results vs. the paginated stations response) instead of
// force-casting the whole record to StationTypes.
const toStationCardData = (station: StationListItem): StationCardTypes => ({
  address: station.address ?? '',
  price: station.price ?? 0,
  status: station.status as StationCardTypes['status'],
  station_id: station._id ?? '',
  code: 'code' in station && station.code != null ? String(station.code) : '',
  connectorTypes: (station.connectorTypes ?? []) as ConnectorTypeEnum[],
});

interface StationSearchResult {
  stations: StationsLocationsTypes[];
  loading: boolean;
  error: boolean;
}

// Mounted only while filters are active, so useStationSearch's fetch effect
// never runs (and never hits the network) while StationsList is showing the
// paginated, unfiltered getStations() list. Lifts the hook's result up to
// the parent via a stable state setter.
const FilteredStationsBridge = ({
  filters,
  onResult,
}: {
  filters: MapFilters;
  onResult: (result: StationSearchResult) => void;
}) => {
  const result = useStationSearch(filters);

  useEffect(() => {
    onResult(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.stations, result.loading, result.error]);

  return null;
};

export const StationsList = () => {
  const [search, setSearch] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [autocompleteOptions, setAutocompleteOptions] = useState<AutocompleteOption[]>([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const [selectedConnectorTypes, setSelectedConnectorTypes] = useState<
    ConnectorTypeEnum[]
  >([]);
  const [availableNow, setAvailableNow] = useState(false);
  const [fastCharging, setFastCharging] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [searchResult, setSearchResult] = useState<StationSearchResult>({
    stations: [],
    loading: false,
    error: false,
  });
  const [stationsResponse, setStationsResponse] =
    useState<StationsResponse | null>(null);
  const [error, setError] = useState<string>('');
  const { page, rowsPerPage, setPage, setRowsPerPage, resetPage } = usePaginationState();

  const { t } = useTranslation();

  const filters: MapFilters = useMemo(
    () => ({
      search,
      connectorTypes: selectedConnectorTypes,
      availableNow,
      fastCharging,
    }),
    [search, selectedConnectorTypes, availableNow, fastCharging],
  );

  const hasActiveFilters = hasAnyFilter(filters);
  const activeFiltersCount = countActiveFilters(filters);

  const fetchAutocompleteOptions = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!query || query.length < 2) {
          setAutocompleteOptions([]);
          return;
        }

        setAutocompleteLoading(true);
        try {
          const response = await ocppService.getStationsLocationsBySearch({
            address: query,
          });
          const options: AutocompleteOption[] = response.map((station) => ({
            address: station.address,
            code: station.code,
            _id: station._id,
          }));
          setAutocompleteOptions(options);
        } catch {
          setAutocompleteOptions([]);
        } finally {
          setAutocompleteLoading(false);
        }
      }, 300),
    [],
  );

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setError('');
      }, 500),
    [],
  );

  const handleInputChange = (_event: SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    fetchAutocompleteOptions(newInputValue);
    debouncedSetSearch(newInputValue);
  };

  const handleAutocompleteChange = (
    _event: SyntheticEvent,
    newValue: AutocompleteOption | string | null,
  ) => {
    if (typeof newValue === 'string') {
      setSearch(newValue);
    } else if (newValue) {
      setSearch(newValue.address);
      setInputValue(newValue.address);
    } else {
      setSearch('');
    }
    setError('');
  };

  const getStationList = async () => {
    try {
      const response = await ocppService.getStations({
        page: page + 1,
        pageSize: rowsPerPage,
        sort: null,
      });
      setStationsResponse(response);
    } catch {
      setError(t('stationPageWithSearch.error'));
    }
  };

  const stationsToRender = useMemo<StationListItem[] | undefined>(() => {
    if (hasActiveFilters && searchResult.stations.length > 0) {
      return searchResult.stations.filter(isStationLocation);
    }
    if (hasActiveFilters && searchResult.stations.length === 0) {
      return [];
    }
    return stationsResponse?.data;
  }, [searchResult.stations, stationsResponse, hasActiveFilters]);

  useEffect(() => {
    getStationList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  // Back to the first page whenever the filters change (activating or clearing
  // them). The actual filtered fetch is delegated to useStationSearch via
  // FilteredStationsBridge below.
  useEffect(() => {
    resetPage();
  }, [search, selectedConnectorTypes, availableNow, fastCharging, resetPage]);

  // Clear stale search results once filters are cleared, so the unfiltered,
  // paginated list takes over immediately instead of showing the last
  // filtered result. Also clears a stale filtered-search error banner so it
  // doesn't linger after the filters that caused it are gone.
  useEffect(() => {
    if (!hasActiveFilters) {
      if (searchResult.error) {
        setError('');
      }
      setSearchResult({ stations: [], loading: false, error: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveFilters]);

  // Surface a filtered-search failure via the same banner an unfiltered
  // fetch failure uses, and clear it once the filtered search succeeds.
  useEffect(() => {
    if (!hasActiveFilters) return;
    if (searchResult.error) {
      setError(t('stationPageWithSearch.error'));
    } else if (!searchResult.loading) {
      setError('');
    }
  }, [hasActiveFilters, searchResult.error, searchResult.loading, t]);

  const toggleConnectorType = (type: ConnectorTypeEnum) => {
    setSelectedConnectorTypes((prev) =>
      prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type]
    );
  };

  const handleResetFilters = () => {
    setAvailableNow(false);
    setFastCharging(false);
    setSelectedConnectorTypes([]);
    setSearch('');
    setInputValue('');
    setAutocompleteOptions([]);
  };

  return (
    <Stack
      sx={{
        padding: '14px 16px',
        maxWidth: '400px',
        width: '100%',
        gap: '12px',
      }}
    >
      <TitleWithBackButton
        title={t('stationPageWithSearch.title')}
        position="center"
      />

      {hasActiveFilters && (
        <FilteredStationsBridge filters={filters} onResult={setSearchResult} />
      )}

      {/* Search Row */}
      <Stack direction="row" spacing={1} alignItems="stretch">
        <Autocomplete
          freeSolo
          fullWidth
          options={autocompleteOptions}
          loading={autocompleteLoading}
          loadingText={t('stationPageWithSearch.loading')}
          noOptionsText={t('stationPageWithSearch.noOptions')}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onChange={handleAutocompleteChange}
          getOptionLabel={(option) => {
            if (typeof option === 'string') {
              return option;
            }
            return option.address;
          }}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              key={option._id || option.address}
              sx={{
                display: 'flex !important',
                flexDirection: 'column !important',
                alignItems: 'flex-start !important',
                justifyContent: 'flex-start !important',
                width: '100%',
                gap: 0.5,
                borderBottom: '1px solid',
                borderColor: 'grey.800',
                '&:last-child': {
                  borderBottom: 'none',
                },
              }}
            >
              <Typography
                variant="body2"
                color="common.white"
                sx={{ fontWeight: 500, width: '100%', textAlign: 'left' }}
              >
                {option.address}
              </Typography>
              {option.code && (
                <Typography
                  variant="caption"
                  color="grey.400"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    width: '100%',
                  }}
                >
                  {t('stationPageWithSearch.stationCode')}:
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'grey.800',
                      borderRadius: '4px',
                      px: 0.75,
                      py: 0.25,
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'green.main',
                    }}
                  >
                    {option.code}
                  </Box>
                </Typography>
              )}
            </Box>
          )}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: 'grey.900',
                border: '1px solid',
                borderColor: 'grey.700',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                borderRadius: '12px',
                mt: 0.5,
                overflow: 'hidden',
                '& .MuiAutocomplete-listbox': {
                  p: 0,
                },
                '& .MuiAutocomplete-option': {
                  py: 1.5,
                  px: 2,
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  '&:hover': {
                    backgroundColor: 'grey.800',
                  },
                  '&[aria-selected="true"]': {
                    backgroundColor: 'grey.800',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'grey.800',
                  },
                },
                '& .MuiAutocomplete-loading': {
                  color: 'grey.400',
                  py: 2,
                },
                '& .MuiAutocomplete-noOptions': {
                  color: 'grey.400',
                  py: 2,
                },
              },
            },
            clearIndicator: {
              sx: {
                color: 'grey.500',
                '&:hover': {
                  color: 'grey.300',
                  backgroundColor: 'grey.800',
                },
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('stationPageWithSearch.searchPlaceholder')}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'grey.500' }} />
                  </InputAdornment>
                ),
              }}
              sx={searchInputStyles}
            />
          )}
          sx={{ flex: 1 }}
        />
        <IconButton
          sx={filterButtonStyles}
          onClick={() => setFilterDrawerOpen(true)}
        >
          <Badge
            badgeContent={activeFiltersCount}
            color="success"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'green.main',
                color: 'common.white',
                fontSize: '10px',
                minWidth: '16px',
                height: '16px',
              },
            }}
          >
            <TuneIcon sx={{ color: 'grey.400' }} />
          </Badge>
        </IconButton>
      </Stack>

      {/* Filter Chips */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          overflowY: 'hidden',
          pb: 0.5,
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <FilterChip
          label={t('stationPageWithSearch.availableNow')}
          active={availableNow}
          onClick={() => setAvailableNow(!availableNow)}
        />
        <FilterChip
          label={t('stationPageWithSearch.fastCharging')}
          active={fastCharging}
          onClick={() => setFastCharging(!fastCharging)}
        />
        {CONNECTOR_TYPE_CHIPS.map((type) => (
          <FilterChip
            key={type}
            label={type}
            active={selectedConnectorTypes.includes(type)}
            onClick={() => toggleConnectorType(type)}
          />
        ))}
      </Box>

      {error ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            px: 3,
            backgroundColor: 'grey.900',
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'error.main',
          }}
        >
          <Typography
            color="error.main"
            variant="body1"
            sx={{ fontWeight: 500, textAlign: 'center' }}
          >
            {error}
          </Typography>
        </Box>
      ) : null}

      {!error && stationsToRender && stationsToRender.length > 0 ? (
        <Stack sx={{ gap: '12px', mt: 1 }}>
          {stationsToRender.map((station) => (
            <Card
              type={CardEnum.Station_card}
              data={toStationCardData(station)}
              key={station._id}
            />
          ))}
        </Stack>
      ) : null}

      {!error &&
       hasActiveFilters &&
       stationsToRender?.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            px: 3,
            backgroundColor: 'grey.900',
            borderRadius: '16px',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: 'grey.800',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SearchIcon sx={{ fontSize: 32, color: 'grey.600' }} />
          </Box>
          <Stack gap={0.5} alignItems="center">
            <Typography
              variant="body1"
              color="common.white"
              sx={{ fontWeight: 600 }}
            >
              {t('stationPageWithSearch.noResults')}
            </Typography>
            <Typography
              variant="body2"
              color="grey.400"
              sx={{ textAlign: 'center' }}
            >
              {t('stationPageWithSearch.tryAdjustingFilters')}
            </Typography>
          </Stack>
          {hasActiveFilters && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleResetFilters}
              sx={{
                mt: 1,
                borderColor: 'grey.700',
                color: 'grey.300',
                borderRadius: '8px',
                '&:hover': {
                  borderColor: 'grey.600',
                  backgroundColor: 'grey.800',
                },
              }}
            >
              {t('stationPageWithSearch.clearFilters')}
            </Button>
          )}
        </Box>
      ) : null}

      {/* Filtered results come back unpaged, so pagination is for the full list only. */}
      {!hasActiveFilters && (
        <Pagination
          tone="dark"
          total={stationsResponse?.total ?? 0}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      )}

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: '85%',
            maxWidth: '320px',
            backgroundColor: 'grey.900',
            backgroundImage: 'none',
          },
        }}
      >
        <Stack sx={{ height: '100%', p: 2 }}>
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Typography variant="h6" fontWeight={600} color="common.white">
              {t('stationPageWithSearch.filters')}
            </Typography>
            <IconButton
              onClick={() => setFilterDrawerOpen(false)}
              sx={{ color: 'grey.400' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Filter Options */}
          <Stack sx={{ flex: 1, gap: 3 }}>
            {/* Quick Filters */}
            <Box>
              <Typography
                variant="subtitle2"
                color="grey.400"
                sx={{ mb: 1.5, textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}
              >
                {t('stationPageWithSearch.quickFilters')}
              </Typography>
              <Stack gap={1}>
                <FilterSwitch
                  label={t('stationPageWithSearch.availableNow')}
                  checked={availableNow}
                  onChange={() => setAvailableNow(!availableNow)}
                />
                <FilterSwitch
                  label={t('stationPageWithSearch.fastCharging')}
                  checked={fastCharging}
                  onChange={() => setFastCharging(!fastCharging)}
                />
              </Stack>
            </Box>

            {/* Connector Types */}
            <Box>
              <Typography
                variant="subtitle2"
                color="grey.400"
                sx={{ mb: 1.5, textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}
              >
                {t('stationPageWithSearch.connectorsTypes')}
              </Typography>
              <Stack gap={0.5}>
                {ALL_CONNECTOR_TYPES.map((type) => (
                  <FormControlLabel
                    key={type}
                    control={
                      <Checkbox
                        checked={selectedConnectorTypes.includes(type)}
                        onChange={() => toggleConnectorType(type)}
                        sx={{
                          color: 'grey.600',
                          '&.Mui-checked': {
                            color: 'green.main',
                          },
                        }}
                      />
                    }
                    label={type}
                    sx={{
                      m: 0,
                      py: 0.5,
                      '& .MuiFormControlLabel-label': {
                        color: 'common.white',
                        fontSize: '14px',
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>

          {/* Footer Buttons */}
          <Stack direction="row" gap={1.5} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleResetFilters}
              sx={{
                borderColor: 'grey.700',
                color: 'grey.300',
                borderRadius: '12px',
                py: 1.5,
                '&:hover': {
                  borderColor: 'grey.600',
                  backgroundColor: 'grey.800',
                },
              }}
            >
              {t('stationPageWithSearch.reset')}
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setFilterDrawerOpen(false)}
              sx={{
                backgroundColor: 'green.main',
                color: 'common.white',
                borderRadius: '12px',
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'green.dark',
                },
              }}
            >
              {t('stationPageWithSearch.apply')}
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </Stack>
  );
};

const SEARCH_ROW_HEIGHT = 48;

const searchInputStyles = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'grey.900',
    borderRadius: '14px',
    height: SEARCH_ROW_HEIGHT,
    transition: 'all 0.2s ease-in-out',
    '& fieldset': {
      borderColor: 'grey.800',
      borderWidth: '1px',
    },
    '&:hover': {
      backgroundColor: 'grey.850',
      '& fieldset': {
        borderColor: 'grey.700',
      },
    },
    '&.Mui-focused': {
      backgroundColor: 'grey.900',
      boxShadow: '0 0 0 2px rgba(0, 209, 129, 0.1)',
      '& fieldset': {
        borderColor: 'green.main',
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputBase-input': {
    color: 'common.white',
    fontSize: '14px',
    fontWeight: 400,
    '&::placeholder': {
      color: 'grey.500',
      opacity: 1,
    },
  },
};

const filterButtonStyles = {
  backgroundColor: 'grey.900',
  borderRadius: '14px',
  width: SEARCH_ROW_HEIGHT,
  height: SEARCH_ROW_HEIGHT,
  minWidth: SEARCH_ROW_HEIGHT,
  border: '1px solid',
  borderColor: 'grey.800',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'grey.850',
    borderColor: 'grey.700',
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
};

